import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ValidatorFn } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { ToastService } from '@techspert-io/user-alerts';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type ChipListStyle = 'solid-border' | 'dashed-border';

@Component({
  selector: 'ct-chip-list',
  template: `
    <mat-form-field
      appearance="outline"
      [class.mat-form-field-invalid]="!!errorMessage"
    >
      <mat-label>{{ label }}</mat-label>
      <mat-chip-list
        [disabled]="disabled"
        class="ct-chipList"
        #chipList
        aria-label="selection"
      >
        <mat-chip
          *ngFor="let item of list"
          [ngClass]="{
            'base-border': chipStyles && chipStyles[item],
            'solid-border': chipStyles && chipStyles[item] === 'solid-border',
            'dashed-border': chipStyles && chipStyles[item] === 'dashed-border'
          }"
          (click)="selectItem(item)"
          [removable]="true"
          (removed)="removeItem(item)"
          data-e2e="chip-list-item"
          [matTooltip]="
            getChipText(item)?.length > 60 ? getChipText(item) : null
          "
        >
          {{ getChipText(item) | ellipsis : 60 }}
          <mat-icon matChipRemove data-e2e="chip-list-item-remove">
            cancel
          </mat-icon>
        </mat-chip>
        <input
          #input
          autocomplete="chrome-off"
          [placeholder]="placeholder"
          [matChipInputFor]="chipList"
          [matAutocomplete]="auto"
          [formControl]="optionsControl"
          (matChipInputTokenEnd)="addItem($event)"
          (paste)="onPaste($event)"
          data-e2e="chip-list-input"
        />
      </mat-chip-list>
      <mat-hint
        *ngIf="errorMessage"
        class="mat-error"
        data-e2e="chip-list-error"
      >
        {{ errorMessage }}
      </mat-hint>
      <mat-hint *ngIf="hint">
        {{ hint }}
      </mat-hint>
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="addSelectedOption($event)"
      >
        <mat-option
          *ngFor="let option of autoCompleteOptions$ | async"
          [value]="option"
          data-e2e="chip-list-option"
        >
          {{ optionDisplayPropKey ? option[optionDisplayPropKey] : option }}
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>
  `,
  styles: [
    `
      .mat-form-field {
        display: block;
      }
      .base-border {
        border-width: 1px;
        border-radius: 16px;
        border-color: #0071ec;
      }
      .solid-border {
        border-style: solid;
      }
      .dashed-border {
        border-style: dashed;
      }
    `,
  ],
})
export class ChipListComponent implements OnInit {
  @Input() label: string;
  @Input() list: unknown[];
  @Input() placeholder: string;
  @Input() displayPropKey: string;
  @Input() optionDisplayPropKey: string;
  @Input() autoCompleteOptions: unknown[] = [];
  @Input() filterAutoCompleteOptions = true;
  @Input() allowOptionsOnly: boolean;
  @Input() disabled: boolean;
  @Input() splitByPipe: boolean = false;
  @Input() errorMessage?: string;
  @Input() chipStyles?: Record<string, ChipListStyle> = {};
  @Input() validators?: ValidatorFn | ValidatorFn[];
  @Input() hint?: string;
  @Output() selectItemSignal = new EventEmitter<unknown>();
  @Output() addItemSignal = new EventEmitter<unknown>();
  @Output() removeItemSignal = new EventEmitter<unknown>();
  @Output() listChange = new EventEmitter<unknown[]>();
  @Output() inputChange = new EventEmitter<unknown>();

  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  autoCompleteOptions$: Observable<unknown>;

  optionsControl = new FormControl();

  private clipboard: Clipboard = navigator.clipboard;

  constructor(private toastService: ToastService) {}

  ngOnInit(): void {
    this.autoCompleteOptions$ = this.optionsControl.valueChanges.pipe(
      map((searchTerm: string | null) =>
        typeof searchTerm === 'string'
          ? this.filterOptions(searchTerm)
          : [...this.autoCompleteOptions]
      )
    );

    if (this.validators) {
      this.optionsControl.setValidators(this.validators);
    }
  }

  addItem(event: MatChipInputEvent): void {
    if (!this.allowOptionsOnly) {
      if (this.optionsControl.valid) {
        const input = event.input;
        const value = event.value;
        this.addItemSignal.emit(value);
        this.optionsControl.setValue(null);
        input.value = '';
      } else {
        this.toastService.sendMessage(
          `Invalid ${this.displayPropKey}`,
          'error'
        );
      }
    }
  }

  selectItem(item: unknown): void {
    this.selectItemSignal.emit(item);
  }

  removeItem(item: unknown): void {
    this.removeItemSignal.emit(item);
  }

  onPaste(event: ClipboardEvent): void {
    if (this.splitByPipe) {
      event.preventDefault();
      this.clipboard.readText().then((text: string) => {
        const terms = Array.from(new Set([...this.list, ...text.split('| ')]));
        this.list = terms;
        this.listChange.emit(terms);
        this.toastService.sendMessage(
          'Attempted to convert pasted content',
          'success'
        );
      });
    }
  }

  addSelectedOption(event: MatAutocompleteSelectedEvent): void {
    this.addItemSignal.emit(event.option.value);
    this.input.nativeElement.value = '';
    this.optionsControl.setValue(null);
  }

  getChipText(item: unknown): string {
    return this.displayPropKey ? item[this.displayPropKey] : item;
  }

  private filterOptions(searchTerm: string): unknown[] {
    if (!this.filterAutoCompleteOptions) {
      return this.autoCompleteOptions;
    }

    return this.autoCompleteOptions.filter((option) =>
      JSON.stringify(option)
        .toLowerCase()
        .includes(searchTerm.toLowerCase().trim())
    );
  }
}
